import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { AlertCircle, FileText, Users, GripVertical, ChevronDown, ChevronUp, ArrowUp, ArrowDown, Pencil, Plus, Search, X, Check, ListChecks } from 'lucide-react';
import { ctaFinalApprovalApi, requestsApi, requestTypeSettingsApi } from '../lib/api';
import { isImageFilename } from '../lib/utils';
import { ReviewStagesEditor, DEFAULT_REVIEW_STAGES, stageDisplay, stagesFromRequest } from './ReviewStagesEditor';
import { toast } from 'sonner';

const ROLE_LABELS = {
  'EXEC_DIRECTOR': 'Executive Director',
  'DIRECTOR': 'Director',
  'COMPANY_REP': 'Company Representative',
  'PI': 'Principal Investigator',
  'CO_PI': 'Co-Investigator',
  'ADMIN': 'Administrator',
  'CRD': 'Admin Support',
  'COORDINATOR': 'Coordinator',
  'INVESTIGATOR': 'Investigator',
  'REVIEWER': 'Reviewer',
  'CONSULTANT': 'Consultant',
};

const roleDisplay = (sig) => {
  if (sig.display_label_override) return sig.display_label_override;
  return ROLE_LABELS[sig.signature_role] || sig.signature_role;
};

export default function CTAFinalApprovalModal({ open, onOpenChange, requestId, onSuccess, editMode = false, roundMode = false, requestType = 'CTA', onSwitchMode, request = null, focusDocumentId = null }) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [signableDocuments, setSignableDocuments] = useState([]);
  const [availableSignatories, setAvailableSignatories] = useState(null);
  const [selectedDocuments, setSelectedDocuments] = useState({});
  const [expandedDocs, setExpandedDocs] = useState({});
  // Phase 3: re-sign-new-version admin toggle (round mode only)
  const [allowResign, setAllowResign] = useState(true);

  // Add-signatory picker state
  const [pickerOpenDocId, setPickerOpenDocId] = useState(null);
  const [eligibleUsers, setEligibleUsers] = useState({ involved: [], others: [] });
  const [pickerSearch, setPickerSearch] = useState('');

  // Confirm dialog state when Chairman picks a non-thread user
  const [pendingAdd, setPendingAdd] = useState(null); // { docId, user, docName }

  // Inline label-override editing state
  const [editingLabel, setEditingLabel] = useState({ docId: null, idx: null, value: '' });
  // iter146 — Review Stages step RESTORED (was iter143-removed). Wizard re-enables Step 1
  // for fresh approvals AND new signature rounds. Edit mode still goes straight to
  // signatures (no review-stage editing during edit).
  const [reviewStages, setReviewStages] = useState([]);
  const [reviewMode, setReviewMode] = useState('sequential');
  const [step, setStep] = useState(1);
  const showWizard = !editMode;

  useEffect(() => {
    if (open && requestId) {
      loadData();
    }
    if (open && !editMode) {
      setStep(1);
      setReviewStages(roundMode
        ? stagesFromRequest(request?.review_stages)
        : [...(DEFAULT_REVIEW_STAGES[requestType] || [])]);
      setReviewMode(request?.review_mode || 'sequential');
    } else if (open) {
      setStep(2);
    }
  }, [open, requestId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const calls = [
        ctaFinalApprovalApi.getSignableDocuments(requestId),
        ctaFinalApprovalApi.getAvailableSignatories(requestId),
      ];
      if (editMode) calls.push(ctaFinalApprovalApi.getSignatureConfig(requestId));

      const responses = await Promise.all(calls);
      const docsRes = responses[0];
      const signatoryRes = responses[1];
      const configRes = editMode ? responses[2] : null;

      // iter140 — signed-output PDFs (doc_type starts with "Signed_") are build artifacts
      // of previous rounds, not source documents.  Hide them entirely from the picker so
      // the Chairman only sees genuine source docs they could (re-)sign.
      // iter143 — when `focusDocumentId` is set (per-doc Edit Signatories), narrow the
      // picker to JUST that one doc (mini-modal experience).
      // iter148e — filter applies in ALL modes (not just roundMode) — the Final Approval
      // modal could also be opened after signed artifacts exist (re-fired final approval
      // from READY_TO_EXECUTE, edit signatories, etc.).
      let visibleDocs = (docsRes.data || []);
      visibleDocs = visibleDocs.filter(d => !(d.doc_type || '').startsWith('Signed_'));
      if (focusDocumentId) visibleDocs = visibleDocs.filter(d => d.id === focusDocumentId);
      setSignableDocuments(visibleDocs);
      setAvailableSignatories(signatoryRes.data);

      if (roundMode) {
        try {
          const rs = await requestTypeSettingsApi.getSignatureRoundsConfig(requestType);
          setAllowResign(rs.data?.allow_resign_new_version !== false);
        } catch { /* keep default */ }
      }

      const existingConfigs = configRes?.data?.configs || [];
      const configByDocId = {};
      existingConfigs.forEach(c => { configByDocId[c.document_id] = c; });

      const initialSelection = {};
      visibleDocs.forEach(doc => {
        const cfg = configByDocId[doc.id];
        if (editMode && cfg && cfg.signatories?.length) {
          // Pre-populate from saved config. Mark every existing row as removable so the Chairman
          // has full control during edit (even auto-pulled rows can be dropped if they were saved).
          const reanchored = cfg.signatories.map((s, idx) => ({
            user_id: s.user_id,
            user_name: s.user_name,
            user_role: s.user_role,
            signature_role: s.signature_role,
            order: s.order ?? (idx + 1),
            enabled: true,
            is_auto: false,
            locked_enabled: false,
            removable: true,
            display_label_override: s.display_label_override || null,
          }));
          initialSelection[doc.id] = {
            selected: true,
            signatories: reanchored,
            // iter139 — Default mode removed from UI; existing 'default' values upgrade
            // to 'sequential' on load so the Select shows a valid pick.
            signingMode: (cfg.signing_mode && cfg.signing_mode !== 'default') ? cfg.signing_mode : 'sequential'
          };
        } else {
          initialSelection[doc.id] = {
            selected: false,
            signatories: getDefaultSignatories(signatoryRes.data),
            signingMode: 'sequential'
          };
        }
      });
      setSelectedDocuments(initialSelection);

      // In edit mode, auto-expand docs that have saved configs so Chairman sees them immediately
      if (editMode) {
        const expanded = {};
        existingConfigs.forEach(c => { expanded[c.document_id] = true; });
        setExpandedDocs(expanded);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load documents and signatories');
    } finally {
      setLoading(false);
    }
  };

  const getDefaultSignatories = (signatoryData) => {
    const defaults = [];
    let order = 1;

    // iter167 Issue 26 — FT requests get a fixed 4-slot default:
    //   1. Executive Director  2. Director  3. FINANCE  4. Financial Officer
    // All four auto-pulled and enabled by default. Chairman can still
    // reorder / add / remove. (FT has no PI / Co-PI / Company Rep
    // signatories — they live exclusively on CTA.)
    if (requestType === 'FT') {
      if (signatoryData.exec_director) {
        defaults.push({
          ...signatoryData.exec_director,
          signature_role: 'EXEC_DIRECTOR',
          order: order++, enabled: true, is_auto: true,
          locked_enabled: false, removable: false, display_label_override: null,
        });
      }
      if (signatoryData.director) {
        defaults.push({
          ...signatoryData.director,
          signature_role: 'DIRECTOR',
          order: order++, enabled: true, is_auto: true,
          locked_enabled: false, removable: false, display_label_override: null,
        });
      }
      if (signatoryData.finance && signatoryData.finance.length > 0) {
        defaults.push({
          ...signatoryData.finance[0],
          signature_role: 'FINANCE',
          order: order++, enabled: true, is_auto: true,
          locked_enabled: false, removable: false, display_label_override: null,
        });
      }
      if (signatoryData.financial_officer) {
        defaults.push({
          ...signatoryData.financial_officer,
          signature_role: 'FINANCIAL_OFFICER',
          order: order++, enabled: true, is_auto: true,
          locked_enabled: false, removable: false, display_label_override: null,
        });
      }
      return defaults;
    }

    if (signatoryData.exec_director) {
      defaults.push({
        ...signatoryData.exec_director,
        signature_role: 'EXEC_DIRECTOR',
        order: order++,
        enabled: true,
        is_auto: true,           // Auto-pulled by system
        locked_enabled: false,   // Chairman may uncheck if not needed for this document
        removable: false,        // Cannot be removed from list (always stays as an "Auto" row)
        display_label_override: null,
      });
    }
    if (signatoryData.director) {
      defaults.push({
        ...signatoryData.director,
        signature_role: 'DIRECTOR',
        order: order++,
        enabled: true,
        is_auto: true,
        locked_enabled: false,
        removable: false,
        display_label_override: null,
      });
    }
    if (signatoryData.company_rep) {
      defaults.push({
        ...signatoryData.company_rep,
        signature_role: 'COMPANY_REP',
        order: order++,
        enabled: true,
        is_auto: true,
        locked_enabled: false,
        removable: false,
        display_label_override: null,
      });
    }
    // Auto-pull PIs from selected_pi_user_ids — enabled by default; Chairman may opt one out per doc.
    signatoryData.pis?.forEach((pi) => {
      defaults.push({
        ...pi,
        signature_role: 'PI',
        order: order++,
        enabled: true,
        is_auto: true,
        locked_enabled: false,
        removable: false,
        display_label_override: null,
      });
    });
    // Auto-pull Co-Investigators from selected_co_investigator_user_ids — UNCHECKED by default,
    // Chairman opts them in. Cannot be removed from the list.
    signatoryData.co_investigators?.forEach((coi) => {
      defaults.push({
        ...coi,
        signature_role: 'CO_PI',
        order: order++,
        enabled: false,
        is_auto: true,
        locked_enabled: false,  // Chairman can toggle this on/off
        removable: false,
        display_label_override: null,
      });
    });

    return defaults;
  };

  // Update the per-document free-text label override for a signatory.
  const updateLabelOverride = (docId, signatoryIndex, value) => {
    setSelectedDocuments(prev => ({
      ...prev,
      [docId]: {
        ...prev[docId],
        signatories: prev[docId].signatories.map((s, i) =>
          i === signatoryIndex
            ? { ...s, display_label_override: value && value.trim() ? value.trim() : null }
            : s
        )
      }
    }));
  };

  // Helper — check if user is a thread member of the doc
  const isUserInDocThread = (docId, userId) => {
    const doc = signableDocuments.find(d => d.id === docId);
    if (!doc) return true; // If we don't have the doc info, don't block
    return (doc.thread_member_ids || []).includes(userId);
  };

  // Add a new signatory (Chairman-added, e.g., a CRD as witness) to a document.
  const addSignatory = (docId, picked) => {
    setSelectedDocuments(prev => {
      const doc = prev[docId];
      if (!doc) return prev;
      // De-dup: skip if user already in the list
      if (doc.signatories.some(s => s.user_id === picked.user_id)) return prev;
      const newSig = {
        user_id: picked.user_id,
        user_name: picked.user_name,
        user_role: picked.user_role,
        signature_role: picked.user_role,  // Auto-derive from system role; Chairman can override label
        order: doc.signatories.length + 1,
        enabled: true,
        is_auto: false,
        locked_enabled: false,
        removable: true,
        display_label_override: null,
        outside_thread: !isUserInDocThread(docId, picked.user_id),
      };
      return {
        ...prev,
        [docId]: { ...doc, signatories: [...doc.signatories, newSig] }
      };
    });
  };

  // Triggered when a user clicks a row in the picker. If the user is NOT involved with the doc thread,
  // surface a confirmation popup before adding.
  const pickSignatory = (docId, picked) => {
    const doc = signableDocuments.find(d => d.id === docId);
    if (!isUserInDocThread(docId, picked.user_id)) {
      setPendingAdd({ docId, user: picked, docName: doc?.filename || 'this document' });
      return;
    }
    addSignatory(docId, picked);
    closeSignatoryPicker();
  };

  const confirmAddOutside = () => {
    if (pendingAdd) {
      addSignatory(pendingAdd.docId, pendingAdd.user);
      setPendingAdd(null);
      closeSignatoryPicker();
    }
  };
  const cancelAddOutside = () => setPendingAdd(null);

  // Remove a signatory. Allowed if:
  //   - the row is Chairman-added (removable=true), OR
  //   - the user is outside this document's discussion thread (auto-pulled but not involved).
  const removeSignatory = (docId, signatoryIndex) => {
    setSelectedDocuments(prev => {
      const doc = prev[docId];
      if (!doc) return prev;
      const target = doc.signatories[signatoryIndex];
      if (!target) return prev;
      const isOutside = !isUserInDocThread(docId, target.user_id);
      if (!target.removable && !isOutside) return prev;
      const arr = doc.signatories.filter((_, i) => i !== signatoryIndex)
        .map((s, i) => ({ ...s, order: i + 1 }));
      return {
        ...prev,
        [docId]: { ...doc, signatories: arr }
      };
    });
  };

  // Open the "Add Signatory" picker for a specific document; lazy-load eligible users.
  const openSignatoryPicker = async (docId) => {
    setPickerOpenDocId(docId);
    setPickerSearch('');
    if (eligibleUsers.involved.length === 0 && eligibleUsers.others.length === 0) {
      try {
        const res = await ctaFinalApprovalApi.getEligibleSignatoryUsers(requestId);
        setEligibleUsers(res.data);
      } catch (e) {
        console.error('Failed to load eligible signatory users', e);
        toast.error('Could not load user list');
      }
    }
  };

  const closeSignatoryPicker = () => {
    setPickerOpenDocId(null);
    setPickerSearch('');
  };

  // Filter helper for the picker
  const filterUsers = (list) => {
    const q = pickerSearch.trim().toLowerCase();
    const currentSigs = pickerOpenDocId ? (selectedDocuments[pickerOpenDocId]?.signatories || []) : [];
    const alreadyIds = new Set(currentSigs.map(s => s.user_id));
    return (list || [])
      .filter(u => !alreadyIds.has(u.user_id))
      .filter(u => {
        if (!q) return true;
        const hay = `${u.user_name} ${u.email || ''} ${u.user_role}`.toLowerCase();
        return hay.includes(q);
      });
  };

  const toggleDocumentSelection = (docId) => {
    setSelectedDocuments(prev => ({
      ...prev,
      [docId]: {
        ...prev[docId],
        selected: !prev[docId].selected
      }
    }));
  };

  const toggleSignatory = (docId, signatoryIndex) => {
    setSelectedDocuments(prev => ({
      ...prev,
      [docId]: {
        ...prev[docId],
        signatories: prev[docId].signatories.map((s, i) => 
          i === signatoryIndex ? { ...s, enabled: !s.enabled } : s
        )
      }
    }));
  };

  const updateSigningMode = (docId, mode) => {
    setSelectedDocuments(prev => ({
      ...prev,
      [docId]: {
        ...prev[docId],
        signingMode: mode
      }
    }));
  };

  // Move a signatory up or down in the array. Only used in Fully Sequential mode.
  const moveSignatory = (docId, index, direction) => {
    setSelectedDocuments(prev => {
      const doc = prev[docId];
      if (!doc) return prev;
      const arr = [...doc.signatories];
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= arr.length) return prev;
      [arr[index], arr[newIndex]] = [arr[newIndex], arr[index]];
      // Re-number the `order` field to match new array position.
      const renumbered = arr.map((s, i) => ({ ...s, order: i + 1 }));
      return {
        ...prev,
        [docId]: { ...doc, signatories: renumbered }
      };
    });
  };

  const toggleDocExpanded = (docId) => {
    setExpandedDocs(prev => ({
      ...prev,
      [docId]: !prev[docId]
    }));
  };

  // Why a document cannot be (re)selected in the current mode — null when selectable.
  const getDocLockReason = (doc) => {
    if (!doc) return null;
    if (editMode && doc.already_signed) return 'Signature collected — locked';
    // iter167 Bug 9 — Always lock already-signed docs across ALL modes (including
    // the default "Final Approval & E-Signature Setup" entry point). Previously
    // the lock only applied in roundMode, which let Chairman/Admin accidentally
    // re-route an already-signed doc through a fresh approval flow.
    if (doc.already_signed) return 'Already signed in a previous round';
    if (roundMode) {
      if (doc.in_pending_config) return 'Already in the current signature round';
      if (doc.prev_version_signed && !allowResign) return 'Re-signing new versions is disabled in Admin Config';
    }
    return null;
  };

  const handleSubmit = async () => {
    // Validate selection (locked docs — already signed — are never part of the payload)
    const selectedDocs = Object.entries(selectedDocuments)
      .filter(([docId, config]) => {
        if (!config.selected) return false;
        const doc = signableDocuments.find(d => d.id === docId);
        return doc && !getDocLockReason(doc);
      })
      .map(([docId, config]) => {
        const doc = signableDocuments.find(d => d.id === docId);
        const enabledSignatories = config.signatories
          .filter(s => s.enabled)
          .map((s, idx) => ({
            user_id: s.user_id,
            user_name: s.user_name,
            user_role: s.user_role,
            signature_role: s.signature_role,
            order: idx + 1,
            display_label_override: s.display_label_override || null,
          }));
        
        return {
          document_id: docId,
          document_name: doc.filename,
          document_type: doc.doc_type,
          version: doc.version,
          signatories: enabledSignatories,
          signing_mode: config.signingMode
        };
      });

    if (selectedDocs.length === 0) {
      toast.error(roundMode
        ? 'Please select at least one new document for the signature round'
        : 'Please select at least one document for e-signature');
      return;
    }

    // Validate each document has at least one signatory
    for (const doc of selectedDocs) {
      if (doc.signatories.length === 0) {
        toast.error(`Document "${doc.document_name}" must have at least one signatory`);
        return;
      }
    }

    // Phase 5: review stages apply at approval AND round time (not edit)
    const stagesPayload = reviewStages.map(s => (s.user_id ? { user_id: s.user_id } : { role: s.role }));
    setSubmitting(true);
    try {
      if (roundMode) {
        const res = await requestsApi.startSignatureRound(requestId, { documents: selectedDocs, review_stages: stagesPayload, review_mode: reviewMode });
        toast.success(res.data?.message || 'New signature round started');
      } else if (editMode) {
        await ctaFinalApprovalApi.editFinalApproval(requestId, selectedDocs);
        toast.success('E-Signature configuration updated');
      } else if (requestType === 'FT') {
        // Phase 4: FT in per-document mode goes through the FT chairman-approve endpoint
        await requestsApi.ftChairmanApprove(requestId, { mode: 'per_document', documents: selectedDocs, review_stages: stagesPayload, review_mode: reviewMode });
        toast.success('Approved — per-document e-signature configured');
      } else {
        await ctaFinalApprovalApi.submitFinalApproval(requestId, selectedDocs, { review_stages: stagesPayload, review_mode: reviewMode });
        toast.success('Final approval submitted with document selection');
      }
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to submit:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCount = Object.entries(selectedDocuments).filter(([docId, d]) => {
    if (!d.selected) return false;
    const doc = signableDocuments.find(x => x.id === docId);
    return doc && !getDocLockReason(doc);
  }).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            {roundMode ? 'Start New Signature Round' : editMode ? 'Edit E-Signature Configuration' : `${requestType} Final Approval & E-Signature Setup`}
          </DialogTitle>
          <DialogDescription>
            {roundMode
              ? 'Select newly approved documents for an additional e-signature round. Previously signed documents stay locked and untouched.'
              : editMode
              ? 'Adjust the signatories or signing order. Documents that already collected a signature are locked.'
              : 'Select approved documents for e-signature and configure signatories'}
          </DialogDescription>
        </DialogHeader>

        {/* iter138 — Single Bundle mode removed; the modal is always per-document. */}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : showWizard && step === 1 ? (
          /* Step 1 of 2 — Review Stages (skippable) */
          <div className="space-y-4" data-testid="esign-wizard-step1">
            <p className="text-sm text-slate-600">
              <span className="font-semibold">Step 1 of 2.</span> Define who reviews this request — in
              order — before e-signatures are collected. Add roles or specific people, or skip to go
              straight to signatures.
            </p>
            <ReviewStagesEditor
              stages={reviewStages}
              onChange={setReviewStages}
              requestType={requestType}
              requestId={requestId}
              mode={reviewMode}
              onModeChange={setReviewMode}
            />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Warning Notice */}
            <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Only approved PDF, Word, and image documents can be selected</p>
                <p className="mt-1">Each selected document will be individually e-signed and generate a separate signed PDF. Image files (PNG, JPG, HEIC, etc.) are auto-converted to a centred A4 PDF page for signing.</p>
              </div>
            </div>

            {/* Document List */}
            {signableDocuments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No approved PDF, Word, or image documents available for e-signature.</p>
                <p className="text-sm mt-1">Ensure documents are approved before final approval.</p>
              </div>
            ) : (
              <div className="space-y-3">
                <Label className="text-sm font-medium">Select Documents for E-Signature:</Label>
                
                {signableDocuments.map(doc => {
                  const lockReason = getDocLockReason(doc);
                  const docIsImage = isImageFilename(doc.filename);
                  return (
                  <Card 
                    key={doc.id} 
                    className={`transition-colors ${lockReason ? 'opacity-70 bg-slate-50' : selectedDocuments[doc.id]?.selected ? 'border-blue-500 bg-blue-50/30' : ''}`}
                  >
                    <CardContent className="p-4">
                      {/* Document Header */}
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id={`doc-${doc.id}`}
                          checked={lockReason && roundMode ? false : (selectedDocuments[doc.id]?.selected || false)}
                          onCheckedChange={() => !lockReason && toggleDocumentSelection(doc.id)}
                          disabled={!!lockReason}
                          className="mt-1"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <label 
                              htmlFor={`doc-${doc.id}`}
                              className="font-medium cursor-pointer truncate"
                            >
                              {doc.filename}
                            </label>
                            <Badge variant="outline" className="text-xs">
                              {doc.doc_type}
                            </Badge>
                            <Badge variant="secondary" className="text-xs">
                              v{doc.version}
                            </Badge>
                            <Badge className="text-xs bg-green-100 text-green-800">
                              Approved
                            </Badge>
                            {docIsImage && !lockReason && (
                              <Badge className="text-xs bg-indigo-100 text-indigo-700 border-indigo-200" data-testid={`cta-doc-image-badge-${doc.id}`}>
                                Image &rarr; PDF
                              </Badge>
                            )}
                            {/* iter163e — Bundle docs from legacy bundle-mode rounds get
                                their own purple chip so Chairman can tell them apart
                                from individually-uploaded sources. */}
                            {doc.is_bundle && (
                              <Badge
                                className="text-xs bg-purple-100 text-purple-700 border-purple-200"
                                data-testid={`cta-doc-bundle-badge-${doc.id}`}
                              >
                                Bundle
                              </Badge>
                            )}
                            {lockReason && (
                              <Badge
                                variant="outline"
                                className="text-xs bg-slate-100 text-slate-600 border-slate-300"
                                data-testid={`doc-lock-badge-${doc.id}`}
                              >
                                {lockReason}
                              </Badge>
                            )}
                            {!lockReason && roundMode && doc.prev_version_signed && (
                              <Badge
                                className="text-xs bg-amber-100 text-amber-800"
                                data-testid={`doc-resign-badge-${doc.id}`}
                              >
                                Re-sign (new version)
                              </Badge>
                            )}
                          </div>
                          
                          {!lockReason && selectedDocuments[doc.id]?.selected && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="mt-2 text-sm h-7"
                              onClick={() => toggleDocExpanded(doc.id)}
                            >
                              <Users className="w-4 h-4 mr-1" />
                              Configure Signatories ({selectedDocuments[doc.id]?.signatories.filter(s => s.enabled).length})
                              {expandedDocs[doc.id] ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Signatory Configuration (Expanded) */}
                      {!lockReason && selectedDocuments[doc.id]?.selected && expandedDocs[doc.id] && (
                        <div className="mt-4 pl-8 space-y-4 border-t pt-4">
                          {/* Signing Mode */}
                          <div className="flex items-center gap-4">
                            <Label className="text-sm">Signing Order:</Label>
                            <Select
                              value={selectedDocuments[doc.id]?.signingMode || 'default'}
                              onValueChange={(v) => updateSigningMode(doc.id, v)}
                            >
                              <SelectTrigger className="w-48 h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="sequential">Fully Sequential</SelectItem>
                                <SelectItem value="parallel">Parallel — everyone signs at once</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Signatories List */}
                          <div className="space-y-2">
                            <Label className="text-sm">
                              Signatories:
                              {selectedDocuments[doc.id]?.signingMode === 'sequential' && (
                                <span className="ml-2 text-xs font-normal text-slate-500">
                                  Use the arrows to reorder signers — they will sign in this exact order.
                                </span>
                              )}
                              {selectedDocuments[doc.id]?.signingMode === 'parallel' && (
                                <span className="ml-2 text-xs font-normal text-emerald-700">
                                  Parallel mode — all signatories receive their signing link at the same time and can sign in any order.
                                </span>
                              )}
                            </Label>
                            {selectedDocuments[doc.id]?.signatories.map((sig, idx) => {
                              const isSequential = selectedDocuments[doc.id]?.signingMode === 'sequential';
                              const totalSigs = selectedDocuments[doc.id]?.signatories.length || 0;
                              const isEditingThisLabel = editingLabel.docId === doc.id && editingLabel.idx === idx;
                              return (
                              <div
                                key={`${sig.user_id}-${idx}`}
                                className={`flex items-center gap-3 p-2 rounded border ${sig.enabled ? 'bg-white' : 'bg-gray-50 opacity-60'}`}
                                data-testid={`signatory-row-${doc.id}-${idx}`}
                              >
                                {isSequential ? (
                                  <div className="flex flex-col gap-0.5">
                                    <button
                                      type="button"
                                      className="text-slate-500 hover:text-blue-700 disabled:text-slate-300 disabled:cursor-not-allowed"
                                      onClick={() => moveSignatory(doc.id, idx, 'up')}
                                      disabled={idx === 0}
                                      data-testid={`signatory-move-up-${doc.id}-${idx}`}
                                      title="Move up"
                                    >
                                      <ArrowUp className="w-4 h-4" />
                                    </button>
                                    <button
                                      type="button"
                                      className="text-slate-500 hover:text-blue-700 disabled:text-slate-300 disabled:cursor-not-allowed"
                                      onClick={() => moveSignatory(doc.id, idx, 'down')}
                                      disabled={idx === totalSigs - 1}
                                      data-testid={`signatory-move-down-${doc.id}-${idx}`}
                                      title="Move down"
                                    >
                                      <ArrowDown className="w-4 h-4" />
                                    </button>
                                  </div>
                                ) : (
                                  <GripVertical
                                    className="w-4 h-4 text-gray-300"
                                    title="Reordering is only available in Fully Sequential mode"
                                  />
                                )}
                                <Checkbox
                                  checked={sig.enabled}
                                  onCheckedChange={() => !sig.locked_enabled && toggleSignatory(doc.id, idx)}
                                  disabled={sig.locked_enabled}
                                  title={sig.locked_enabled ? 'Mandatory signatory — cannot be disabled' : ''}
                                  data-testid={`signatory-enable-${doc.id}-${idx}`}
                                />
                                <span className="text-sm font-medium w-8">{idx + 1}.</span>
                                <div className="flex-1 flex items-center gap-2 flex-wrap min-w-0">
                                  <span className="text-sm font-medium truncate">{sig.user_name}</span>
                                  {!isUserInDocThread(doc.id, sig.user_id) && (
                                    <AlertCircle
                                      className="w-3.5 h-3.5 text-amber-600 flex-shrink-0"
                                      aria-label="Not involved in this document's thread"
                                      title="This signatory is not part of this document's discussion thread"
                                      data-testid={`signatory-outside-thread-warn-${doc.id}-${idx}`}
                                    />
                                  )}
                                  {/* Label badge — auto-derived from signature_role, or override if set */}
                                  {isEditingThisLabel ? (
                                    <div className="flex items-center gap-1">
                                      <Input
                                        autoFocus
                                        className="h-7 text-xs w-[180px]"
                                        value={editingLabel.value}
                                        onChange={(e) => setEditingLabel({ ...editingLabel, value: e.target.value })}
                                        placeholder={ROLE_LABELS[sig.signature_role] || sig.signature_role}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            updateLabelOverride(doc.id, idx, editingLabel.value);
                                            setEditingLabel({ docId: null, idx: null, value: '' });
                                          }
                                          if (e.key === 'Escape') {
                                            setEditingLabel({ docId: null, idx: null, value: '' });
                                          }
                                        }}
                                        data-testid={`signatory-label-input-${doc.id}-${idx}`}
                                      />
                                      <button
                                        type="button"
                                        className="text-emerald-600 hover:text-emerald-700"
                                        onClick={() => {
                                          updateLabelOverride(doc.id, idx, editingLabel.value);
                                          setEditingLabel({ docId: null, idx: null, value: '' });
                                        }}
                                        title="Save label"
                                        data-testid={`signatory-label-save-${doc.id}-${idx}`}
                                      >
                                        <Check className="w-4 h-4" />
                                      </button>
                                      <button
                                        type="button"
                                        className="text-slate-500 hover:text-slate-700"
                                        onClick={() => setEditingLabel({ docId: null, idx: null, value: '' })}
                                        title="Cancel"
                                      >
                                        <X className="w-4 h-4" />
                                      </button>
                                    </div>
                                  ) : (
                                    <>
                                      <Badge
                                        variant="outline"
                                        className={`text-xs ${sig.display_label_override ? 'bg-amber-50 border-amber-300 text-amber-800' : ''}`}
                                        data-testid={`signatory-label-badge-${doc.id}-${idx}`}
                                      >
                                        {roleDisplay(sig)}
                                      </Badge>
                                      <button
                                        type="button"
                                        className="text-slate-400 hover:text-blue-700"
                                        onClick={() => setEditingLabel({
                                          docId: doc.id,
                                          idx,
                                          value: sig.display_label_override || ''
                                        })}
                                        title="Edit label on the signed PDF (per document)"
                                        data-testid={`signatory-label-edit-${doc.id}-${idx}`}
                                      >
                                        <Pencil className="w-3.5 h-3.5" />
                                      </button>
                                    </>
                                  )}
                                </div>
                                {/* Remove button — Chairman-added rows OR auto-pulled rows whose user is
                                    outside the document thread (mismatched signatory the Chairman likely
                                    wants to drop). Otherwise show the "Auto" tag. */}
                                {(sig.removable || !isUserInDocThread(doc.id, sig.user_id)) ? (
                                  <button
                                    type="button"
                                    className="text-red-500 hover:text-red-700"
                                    onClick={() => removeSignatory(doc.id, idx)}
                                    title={sig.removable
                                      ? 'Remove signatory'
                                      : 'This signatory is not in the document thread — click to remove'}
                                    data-testid={`signatory-remove-${doc.id}-${idx}`}
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                ) : (
                                  <span
                                    className="text-[10px] text-slate-400 uppercase tracking-wide"
                                    title="Auto-pulled from request — cannot be removed"
                                  >
                                    Auto
                                  </span>
                                )}
                              </div>
                              );
                            })}

                            {/* Add Signatory CTA */}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="w-full justify-center text-xs"
                              onClick={() => openSignatoryPicker(doc.id)}
                              data-testid={`add-signatory-btn-${doc.id}`}
                            >
                              <Plus className="w-3.5 h-3.5 mr-1" />
                              Add Signatory
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            )}

            {/* Phase 5: step-2 summary of the configured review stages */}
            {showWizard && (
              <div className="border-t pt-3 flex items-center gap-2 text-xs text-slate-600 flex-wrap" data-testid="esign-wizard-stages-summary">
                <ListChecks className="w-3.5 h-3.5 shrink-0" />
                <span>
                  Review stages:{' '}
                  <span className="font-medium">
                    {reviewStages.length ? reviewStages.map(stageDisplay).join(' → ') : 'None — straight to e-signatures'}
                  </span>
                </span>
                <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => setStep(1)} data-testid="esign-wizard-edit-stages-btn">
                  Edit
                </Button>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="mt-4 flex items-center justify-between">
          {showWizard && step === 1 ? (
            <>
              <div className="text-sm text-gray-500">Step 1 of 2 — Review Stages</div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                <Button variant="outline" onClick={() => { setReviewStages([]); setStep(2); }} data-testid="esign-wizard-skip-btn">
                  Skip — no review stages
                </Button>
                <Button onClick={() => setStep(2)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="esign-wizard-next-btn">
                  Next: Configure Signatures
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="text-sm text-gray-500">
                {selectedCount > 0 && `${selectedCount} document(s) selected`}
              </div>
              <div className="flex gap-2">
                {showWizard && (
                  <Button variant="outline" onClick={() => setStep(1)} disabled={submitting} data-testid="esign-wizard-back-btn">
                    Back
                  </Button>
                )}
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSubmit} 
                  disabled={submitting || selectedCount === 0}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="cta-esign-submit-btn"
                >
                  {submitting
                    ? (roundMode ? 'Starting…' : editMode ? 'Saving…' : 'Submitting...')
                    : (roundMode ? 'Start Round' : editMode ? 'Save Changes' : 'Proceed to E-Signature')}
                </Button>
              </div>
            </>
          )}
        </DialogFooter>
      </DialogContent>

      {/* Add Signatory Picker Sub-Dialog */}
      <Dialog open={!!pickerOpenDocId} onOpenChange={(o) => { if (!o) closeSignatoryPicker(); }}>
        <DialogContent className="max-w-xl" data-testid="add-signatory-dialog">
          <DialogHeader>
            <DialogTitle>Add Signatory</DialogTitle>
            <DialogDescription>
              Pick a user to add to this document's signature list. Their role label appears on the signed PDF — edit it after adding with the pencil icon.
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <Input
              autoFocus
              className="pl-9"
              placeholder="Search by name, email, or role…"
              value={pickerSearch}
              onChange={(e) => setPickerSearch(e.target.value)}
              data-testid="add-signatory-search"
            />
          </div>
          <Tabs defaultValue="involved" className="mt-2">
            <TabsList className="grid grid-cols-2">
              <TabsTrigger value="involved" data-testid="add-signatory-tab-involved">
                Involved in this request ({filterUsers(eligibleUsers.involved).length})
              </TabsTrigger>
              <TabsTrigger value="others" data-testid="add-signatory-tab-others">
                All active users ({filterUsers(eligibleUsers.others).length})
              </TabsTrigger>
            </TabsList>
            <TabsContent value="involved" className="max-h-[40vh] overflow-y-auto space-y-1 mt-2">
              {filterUsers(eligibleUsers.involved).length === 0 ? (
                <p className="text-sm text-slate-500 italic p-3 text-center">No matching users.</p>
              ) : filterUsers(eligibleUsers.involved).map(u => {
                const outside = pickerOpenDocId && !isUserInDocThread(pickerOpenDocId, u.user_id);
                return (
                <button
                  type="button"
                  key={u.user_id}
                  onClick={() => pickSignatory(pickerOpenDocId, u)}
                  className="w-full flex items-center justify-between p-2 rounded hover:bg-blue-50 text-left border border-transparent hover:border-blue-200 transition"
                  data-testid={`add-signatory-pick-${u.user_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate flex items-center gap-1.5">
                      {u.user_name}
                      {outside && (
                        <AlertCircle
                          className="w-3.5 h-3.5 text-amber-600 flex-shrink-0"
                          aria-label="Not involved in this document's thread"
                          data-testid={`add-signatory-warn-${u.user_id}`}
                        />
                      )}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{u.email}</div>
                  </div>
                  <Badge variant="outline" className="text-[10px] flex-shrink-0 ml-2">
                    {ROLE_LABELS[u.user_role] || u.user_role}
                  </Badge>
                </button>
              );})}
            </TabsContent>
            <TabsContent value="others" className="max-h-[40vh] overflow-y-auto space-y-1 mt-2">
              {filterUsers(eligibleUsers.others).length === 0 ? (
                <p className="text-sm text-slate-500 italic p-3 text-center">No matching users.</p>
              ) : filterUsers(eligibleUsers.others).map(u => {
                const outside = pickerOpenDocId && !isUserInDocThread(pickerOpenDocId, u.user_id);
                return (
                <button
                  type="button"
                  key={u.user_id}
                  onClick={() => pickSignatory(pickerOpenDocId, u)}
                  className="w-full flex items-center justify-between p-2 rounded hover:bg-blue-50 text-left border border-transparent hover:border-blue-200 transition"
                  data-testid={`add-signatory-pick-${u.user_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate flex items-center gap-1.5">
                      {u.user_name}
                      {outside && (
                        <AlertCircle
                          className="w-3.5 h-3.5 text-amber-600 flex-shrink-0"
                          aria-label="Not involved in this document's thread"
                          data-testid={`add-signatory-warn-${u.user_id}`}
                        />
                      )}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{u.email}</div>
                  </div>
                  <Badge variant="outline" className="text-[10px] flex-shrink-0 ml-2">
                    {ROLE_LABELS[u.user_role] || u.user_role}
                  </Badge>
                </button>
              );})}
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={closeSignatoryPicker}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation for adding a non-involved signatory */}
      <AlertDialog open={!!pendingAdd} onOpenChange={(o) => { if (!o) cancelAddOutside(); }}>
        <AlertDialogContent data-testid="add-signatory-outside-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              Add user outside the document thread?
            </AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium text-slate-900">{pendingAdd?.user?.user_name}</span> is not part of
              the <span className="font-medium text-slate-900">{pendingAdd?.docName}</span> discussion thread.
              Adding them will grant them visibility to this document and its signatures. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="add-signatory-outside-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmAddOutside}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="add-signatory-outside-confirm-btn"
            >
              Add anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
